import { Fragment, useEffect, useState } from "react";

import { rolesApi } from "@/api/roles";
import { apiClient } from "@/api/client";
import { Alert } from "@/components/Alert";
import { FormSelect } from "@/components/FormSelect";
import { PageBody, PageHeader, PageShell } from "@/components/PageShell";
import { PageSkeleton } from "@/components/PageSkeleton";
import { Toast } from "@/components/Toast";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

function cloneMatrix(matrix) {
  return Object.fromEntries(
    Object.entries(matrix || {}).map(([role, keys]) => [role, [...(keys || [])]]),
  );
}

function samePermissionSet(a, b) {
  const left = [...(a || [])].sort();
  const right = [...(b || [])].sort();
  if (left.length !== right.length) return false;
  return left.every((key, i) => key === right[i]);
}

export function RolesPermissionsPage() {
  const [agencies, setAgencies] = useState([]);
  const [selectedAgencyId, setSelectedAgencyId] = useState("");
  const [data, setData] = useState(null);
  const [baselineMatrix, setBaselineMatrix] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [savingRole, setSavingRole] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(null);

  useEffect(() => {
    apiClient
      .get("/agencies/")
      .then((res) => {
        const results = res.data.results || res.data;
        setAgencies(results);
        if (results.length > 0) {
          setSelectedAgencyId(results[0].id);
        }
      })
      .catch(() => {
        setError("Failed to load organizations.");
      });
  }, []);

  useEffect(() => {
    if (selectedAgencyId) {
      loadData(selectedAgencyId);
    }
  }, [selectedAgencyId]);

  const loadData = async (agencyId) => {
    setLoading(true);
    setError(null);
    try {
      const response = await rolesApi.getPermissions(agencyId);
      setData(response);
      setBaselineMatrix(cloneMatrix(response.matrix));
    } catch (err) {
      setError(err.message || "Failed to load roles and permissions.");
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (roleValue, permissionKey, isGranted) => {
    setData((prev) => {
      const newMatrix = { ...prev.matrix };
      if (isGranted) {
        newMatrix[roleValue] = [...(newMatrix[roleValue] || []), permissionKey];
      } else {
        newMatrix[roleValue] = (newMatrix[roleValue] || []).filter((k) => k !== permissionKey);
      }
      return { ...prev, matrix: newMatrix };
    });
  };

  const isRoleDirty = (roleValue) => {
    if (!data?.matrix || !baselineMatrix) return false;
    return !samePermissionSet(data.matrix[roleValue], baselineMatrix[roleValue]);
  };

  const handleSaveRole = async (roleValue) => {
    if (!isRoleDirty(roleValue)) return;

    setSavingRole(roleValue);
    setSaveSuccess(null);
    setError(null);
    try {
      await rolesApi.updateRolePermissions(selectedAgencyId, roleValue, data.matrix[roleValue]);
      setBaselineMatrix((prev) => ({
        ...prev,
        [roleValue]: [...(data.matrix[roleValue] || [])],
      }));
      setSaveSuccess(
        `Successfully updated permissions for ${data.roles.find((r) => r.value === roleValue)?.label}.`,
      );
    } catch (err) {
      setError(`Failed to save permissions for role: ${err.message}`);
    } finally {
      setSavingRole(null);
    }
  };

  if (loading && !data) {
    return <PageSkeleton />;
  }

  const agencyOptions = agencies.map((agency) => ({
    value: agency.id,
    label: agency.name,
  }));

  return (
    <PageShell>
      <Toast message={saveSuccess} onClose={() => setSaveSuccess(null)} />

      <PageHeader
        title="Roles & Permissions"
        description="Configure access levels for roles across the platform."
        actions={
          <div className="flex min-w-[12rem] items-center gap-2">
            <label htmlFor="roles-org-select" className="text-sm font-medium text-muted-foreground">
              Organization
            </label>
            <FormSelect
              id="roles-org-select"
              value={selectedAgencyId}
              onValueChange={setSelectedAgencyId}
              options={agencyOptions}
              placeholder="Select organization…"
              disabled={loading}
              triggerClassName="min-w-[12rem]"
            />
          </div>
        }
      />

      <PageBody>
        <Alert>{error}</Alert>

        {loading && data ? (
          <p className="py-8 text-center text-sm text-muted-foreground">Loading matrix...</p>
        ) : null}

        {!loading && data ? (
          <Card className="gap-4 py-4">
            <CardHeader className="px-4 pb-0">
              <CardTitle className="text-base">Permissions matrix</CardTitle>
              <CardDescription>
                Toggle features per role, then save each role when its column has changes.
              </CardDescription>
            </CardHeader>
            <CardContent className="px-4">
              <div className="overflow-x-auto rounded-lg border border-border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="min-w-[14rem] w-[35%]">Feature</TableHead>
                      {data.roles.map((role) => {
                        const dirty = isRoleDirty(role.value);
                        return (
                          <TableHead key={role.value} className="min-w-[8rem] text-center">
                            <div className="flex flex-col items-center gap-2 py-1">
                              <span>{role.label}</span>
                              <Button
                                type="button"
                                size="sm"
                                onClick={() => handleSaveRole(role.value)}
                                disabled={!dirty || savingRole === role.value}
                              >
                                {savingRole === role.value ? "Saving..." : "Save"}
                              </Button>
                            </div>
                          </TableHead>
                        );
                      })}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.groups.map((group) => (
                      <Fragment key={group.key}>
                        <TableRow className="bg-muted/40 hover:bg-muted/40">
                          <TableCell colSpan={data.roles.length + 1} className="py-4">
                            <div className="font-semibold">{group.label}</div>
                            <p className="mt-1 text-sm text-muted-foreground">
                              {group.description}
                            </p>
                          </TableCell>
                        </TableRow>
                        {group.permissions.map((permKey) => {
                          const permDef = data.permissions.find((p) => p.key === permKey);
                          return (
                            <TableRow key={permKey}>
                              <TableCell className="font-medium">
                                {permDef?.label || permKey}
                              </TableCell>
                              {data.roles.map((role) => {
                                const isGranted = (data.matrix[role.value] || []).includes(
                                  permKey,
                                );
                                return (
                                  <TableCell key={role.value} className="text-center">
                                    <div className="flex justify-center">
                                      <Checkbox
                                        checked={isGranted}
                                        onCheckedChange={(checked) =>
                                          handleToggle(role.value, permKey, checked === true)
                                        }
                                        aria-label={`${role.label}: ${permDef?.label || permKey}`}
                                      />
                                    </div>
                                  </TableCell>
                                );
                              })}
                            </TableRow>
                          );
                        })}
                      </Fragment>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        ) : null}
      </PageBody>
    </PageShell>
  );
}
